import React, { useState, useEffect, useRef } from 'react';
import { Search, BookOpen, X } from 'lucide-react';
import { api } from '../services/api';

interface Treinamento {
  id: string;
  treinamento: string;
  descricao: string;
}

interface TreinamentoSelectFieldProps {
  value?: string;
  onChange: (treinamentoId: string, treinamentoNome: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
}

export function TreinamentoSelectField({
  value = '',
  onChange,
  placeholder = 'Digite o nome do treinamento...',
  disabled = false,
  error,
  label,
  required = false
}: TreinamentoSelectFieldProps) {
  const [treinamentos, setTreinamentos] = useState<Treinamento[]>([]);
  const [filteredTreinamentos, setFilteredTreinamentos] = useState<Treinamento[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTreinamento, setSelectedTreinamento] = useState<Treinamento | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTreinamentos();
  }, []);

  useEffect(() => {
    if (value && treinamentos.length > 0) {
      const treinamento = treinamentos.find(t => t.id === value);
      if (treinamento) {
        setSelectedTreinamento(treinamento);
        setSearchTerm(treinamento.treinamento);
      }
    } else {
      setSelectedTreinamento(null);
      setSearchTerm('');
    }
  }, [value, treinamentos]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = treinamentos.filter(t =>
        t.treinamento.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTreinamentos(filtered);
    } else {
      setFilteredTreinamentos(treinamentos.slice(0, 10));
    }
  }, [searchTerm, treinamentos]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTreinamentos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/treinamentos');
      const data = response.data;
      setTreinamentos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar treinamentos:', err);
      setTreinamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);

    if (!newValue.trim()) {
      setSelectedTreinamento(null);
      onChange('', '');
    }
  };

  const handleSelectTreinamento = (treinamento: Treinamento) => {
    setSelectedTreinamento(treinamento);
    setSearchTerm(treinamento.treinamento);
    setIsOpen(false);
    onChange(treinamento.id, treinamento.treinamento);
  };

  const handleClearSelection = () => {
    setSelectedTreinamento(null);
    setSearchTerm('');
    setIsOpen(false);
    onChange('', '');
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-muted-foreground">
          {label}
          {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={`
              w-full px-3 py-2 pr-10 border border-border rounded-md shadow-sm focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground
              ${error ? 'border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500' : ''}
              ${disabled ? 'bg-muted cursor-not-allowed' : ''}
              ${loading ? 'cursor-wait' : ''}
            `}
          />

          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {selectedTreinamento ? (
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={disabled}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <X size={16} />
              </button>
            ) : (
              <Search size={16} className="text-muted-foreground" />
            )}
          </div>
        </div>

        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {loading ? (
              <div className="px-3 py-2 text-center text-muted-foreground">
                Carregando treinamentos...
              </div>
            ) : filteredTreinamentos.length > 0 ? (
              filteredTreinamentos.map((treinamento) => (
                <button
                  key={treinamento.id}
                  type="button"
                  onClick={() => handleSelectTreinamento(treinamento)}
                  className="w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none flex items-center space-x-2"
                >
                  <BookOpen size={16} className="text-muted-foreground shrink-0" />
                  <div>
                    <span className="font-medium text-foreground">{treinamento.treinamento}</span>
                    {treinamento.descricao && (
                      <span className="ml-2 text-sm text-muted-foreground truncate">
                        {treinamento.descricao}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-center text-muted-foreground">
                {searchTerm.trim() ? 'Nenhum treinamento encontrado' : 'Digite para buscar treinamentos'}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
